import React, { useEffect, useState } from "react";
import {
    Box,
    ButtonBase,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import StatusScreen from "../../../StatusScreen";
import Category from "../../../../api/Category";
import Service from "../../../../api/Service";
import { useCookies } from "react-cookie";
import dayjs from "dayjs";
import TabHeader from "../../../TabHeader";
import { useNavigate } from "react-router-dom";
import TagFilterBar from "../../../TagFilterBar";
import FormattedTime from "../../../FormattedTime";
import FormattedPrice from "../../../FormattedPrice";
import useCurrentLanguage from "../../../../hooks/useCurrentLanguage";
import { pickLocalized } from "../../../../utils/localize";
import useLocalizedDescription from "../../../../hooks/useLocalizedDescription";
import { FONTS, CARD_STYLES } from "../../../../constants/styles";
import BookingForClientChip from "./BookingForClientChip";

const ServiceSelect = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [categories, setCategories] = useState<Category[]>([]);
    const [services, setServices] = useState<Record<string, Service[]>>({});
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    // For easier navigation
    const [selectedCategoryTag, setSelectedCategoryTag] = useState("All");

    useEffect(() => {
        sessionStorage.removeItem("service_id");

        const start = sessionStorage.getItem("slot_start");
        const end = sessionStorage.getItem("slot_end");
        if (start === null || end === null) {
            navigate("/calendar");
        }

        const user_id = sessionStorage.getItem("user_id");
        if (user_id === null) {
            navigate("/calendar/add/user");
        }

        if (!uuid || !brand_id) return;

        const now = dayjs()
            .set("hour", dayjs().get("hour") + 1)
            .set("minutes", 0)
            .set("seconds", 0)
            .set("milliseconds", 0);
        const fullDuration = dayjs(end).diff(dayjs(start), "minutes");
        const calculatedDuration = Math.min(
            fullDuration,
            dayjs(end).diff(now, "minutes")
        );
        setDuration(calculatedDuration);

        Category.list(uuid, brand_id, calculatedDuration, true)
            .then((res) => {
                const enabled = (res ?? []).filter((x: Category) => x.enabled);
                setCategories(enabled);

                if (enabled.length === 0) {
                    setIsLoading(false);
                    return;
                }

                const servicePromises = enabled.map((c: Category) =>
                    Service.list(uuid, brand_id, c.key, calculatedDuration, true)
                        .then((services) => ({
                            categoryKey: c.key,
                            services: (services ?? []).filter((x: Service) => x.enabled),
                        }))
                );

                Promise.all(servicePromises).then((results) => {
                    const serviceMap = Object.fromEntries(
                        results.map(({ categoryKey, services }) => [categoryKey, services])
                    );
                    setServices(serviceMap);
                    setIsLoading(false);
                });
            })
            .catch(console.error);
    }, [uuid, brand_id, navigate]);

    const onSelect = (service: Service) => {
        sessionStorage.setItem("service_id", service.key);
        navigate("/calendar/add/confirmation");
    };

    const currentLang = useCurrentLanguage();

    const categoryTags = ["All", ...categories.map((c) => c.name)];

    const categoryDisplayTags = [
        "All",
        ...categories.map((c) =>
            pickLocalized(c.name, c.nameOriginal, currentLang)
        ),
    ];

    return (
        <>
            <TabHeader
                name="Pick a service"
                onClick={() => navigate("/calendar/add/user")}
            />
            <BookingForClientChip />
            {duration <= 0 ? (
                <StatusScreen
                    icon={HistoryRounded}
                    iconColor={theme.palette.primary.main}
                    title="Time travel isn't available yet"
                    subtitle="Looks like you're trying to schedule an appointment in the past. Let's bring you back to the future!"
                    buttonLabel="Go Back to Calendar"
                    onButtonClick={() => navigate("/calendar")}
                />
            ) : (
                <Stack flex={1} sx={{ overflowX: "hidden", overflowY: "auto", minHeight: 0 }}>
                    {!isLoading && (
                        <Box sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 4,
                            bgcolor: "white",
                            py: 0.5,
                        }}>
                            <TagFilterBar
                                tags={categoryTags}
                                displayTags={categoryDisplayTags}
                                selectedTag={selectedCategoryTag}
                                onSelectTag={setSelectedCategoryTag}
                            />
                        </Box>
                    )}
                    <Stack
                        sx={{
                            pb: "200px",
                            "@keyframes fadeSlideIn": {
                                from: {
                                    opacity: 0,
                                    transform: "translateY(12px)",
                                },
                                to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                },
                            },
                            animation: isLoading ? undefined : "fadeSlideIn 0.3s ease-out",
                        }}
                    >
                        {isLoading ? (
                            <Stack sx={{ pt: 1 }}>
                                {[0, 1].map((sectionIdx) => (
                                    <Stack key={sectionIdx}>
                                        {/* Section header skeleton */}
                                        <Box px={2} py={2}>
                                            <Skeleton
                                                variant="rounded"
                                                width={sectionIdx === 0 ? 120 : 90}
                                                height={20}
                                                sx={{ borderRadius: "6px" }}
                                            />
                                        </Box>
                                        {/* Service card skeletons */}
                                        {Array.from({ length: sectionIdx === 0 ? 3 : 2 }).map(
                                            (_, cardIdx) => (
                                                <Stack
                                                    key={cardIdx}
                                                    direction="row"
                                                    spacing={1.5}
                                                    alignItems="center"
                                                    sx={{
                                                        ...CARD_STYLES,
                                                        p: 1.5,
                                                        mx: 2,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Skeleton
                                                        variant="rounded"
                                                        width={64}
                                                        height={64}
                                                        sx={{
                                                            borderRadius: "14px",
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Stack
                                                        spacing={0.75}
                                                        flex={1}
                                                    >
                                                        <Skeleton
                                                            variant="rounded"
                                                            width={`${55 + cardIdx * 15}%`}
                                                            height={16}
                                                            sx={{ borderRadius: "4px" }}
                                                        />
                                                        <Stack
                                                            direction="row"
                                                            spacing={1.5}
                                                        >
                                                            <Skeleton
                                                                variant="rounded"
                                                                width={40}
                                                                height={14}
                                                                sx={{ borderRadius: "4px" }}
                                                            />
                                                            <Skeleton
                                                                variant="rounded"
                                                                width={50}
                                                                height={14}
                                                                sx={{ borderRadius: "4px" }}
                                                            />
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            )
                                        )}
                                    </Stack>
                                ))}
                            </Stack>
                        ) : (
                            categories
                                .filter(
                                    (x) =>
                                        selectedCategoryTag === "All" ||
                                        x.name === selectedCategoryTag
                                )
                                .map((x) => (
                                    <CategoryView
                                        key={x.key}
                                        category={x}
                                        services={services[x.key] ?? []}
                                        onSelect={onSelect}
                                    />
                                ))
                        )}
                    </Stack>
                </Stack>
            )}
        </>
    );
};

const CategoryView = (props: {
    category: Category;
    services: Service[];
    onSelect: (val: Service) => void;
}) => {
    const { category, services, onSelect } = props;

    const categoryName = useLocalizedDescription(
        category.name,
        category.nameOriginal
    );

    return (
        <Stack key={category.name}>
            <Typography
                padding={2}
                fontWeight={600}
                fontSize="18px"
                lineHeight="23.4px"
                fontFamily={FONTS.display}
                minWidth={0}
                sx={{
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                }}
            >
                {categoryName}
            </Typography>
            {services.map((x) => (
                <Stack key={x.key} mb={0.5} px={2}>
                    <ServiceRow service={x} onSelect={onSelect} />
                </Stack>
            ))}
        </Stack>
    );
};

const ServiceRow = (props: {
    service: Service;
    onSelect: (val: Service) => void;
}) => {
    const theme = useTheme();
    const { service, onSelect } = props;

    const serviceName = useLocalizedDescription(
        service.name,
        service.nameOriginal
    );

    return (
        <ButtonBase
            sx={{
                padding: 0,
                margin: 0,
                borderRadius: "16px",
                border: "none",
                textTransform: "none",
                textAlign: "start",
                width: "100%",
                "&:active .service-card": {
                    transform: "scale(0.98)",
                },
            }}
            onClick={() => onSelect(service)}
        >
            <Stack
                className="service-card"
                direction="row"
                spacing={1.5}
                alignItems="center"
                width="100%"
                sx={{
                    ...CARD_STYLES,
                    p: 1.5,
                    transition: "transform 0.15s ease",
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        minWidth: 64,
                        height: 64,
                        minHeight: 64,
                        borderRadius: "14px",
                        bgcolor: theme.palette.primary.main,
                        backgroundImage: `url(${service.image})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        flex: "0 0 auto",
                    }}
                />
                <Stack
                    spacing={0.75}
                    justifyContent="center"
                    flex={1}
                    minWidth={0}
                >
                    <Typography
                        fontWeight={600}
                        fontSize="16px"
                        lineHeight="20px"
                        fontFamily={FONTS.display}
                        minWidth={0}
                        sx={{
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                        }}
                    >
                        {serviceName}
                    </Typography>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                    >
                        <FormattedPrice
                            price={service.price}
                            currency={service.currency}
                        />
                        <FormattedTime time={service.duration} size="small" />
                    </Stack>
                </Stack>
                <ChevronRightRounded
                    sx={{
                        fontSize: 20,
                        color: "text.secondary",
                        opacity: 0.4,
                        flexShrink: 0,
                    }}
                />
            </Stack>
        </ButtonBase>
    );
};

export default ServiceSelect;
